import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { listProspects, createProspect, deleteProspect, updateProspectCode, sendProspectLink, listActivity, exportProspectsCsv } from "@/lib/api";
import {
  ArrowLeft, Plus, Inbox as InboxIcon, RefreshCw, Edit3, Star,
} from "lucide-react";
import { ActivityFeed } from "@/components/admin/ActivityFeed";
import { GiggleCallout } from "@/components/admin/GiggleCallout";
import { NewProspectForm } from "@/components/admin/NewProspectForm";
import { ProspectRow } from "@/components/admin/ProspectRow";

const EMPTY_FORM = { company_name: "", contact_name: "", contact_email: "", logo_url: "", notes: "" };

export default function Admin() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [creating, setCreating] = useState(false);
  const [activity, setActivity] = useState([]);
  const [activityBusy, setActivityBusy] = useState(false);
  const nav = useNavigate();
  const origin = typeof window !== "undefined" ? window.location.origin : "";

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const data = await listProspects();
      setItems(data || []);
    } catch {
      setItems([]);
    } finally {
      setBusy(false);
    }
  }, []);

  const loadActivity = useCallback(async () => {
    setActivityBusy(true);
    try {
      const data = await listActivity(40);
      setActivity(data?.events || []);
    } catch {
      setActivity([]);
    } finally {
      setActivityBusy(false);
    }
  }, []);

  useEffect(() => { load(); loadActivity(); }, [load, loadActivity]);
  useEffect(() => {
    const t = setInterval(loadActivity, 20000);
    return () => clearInterval(t);
  }, [loadActivity]);

  const handleCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Copy failed");
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.company_name.trim()) { toast.error("Company name is required"); return; }
    setCreating(true);
    try {
      const p = await createProspect(form);
      toast.success(`Created · code ${p.code}`);
      setItems((curr) => [p, ...curr]);
      setForm(EMPTY_FORM);
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create prospect");
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id, company) => {
    if (!window.confirm(`Delete prospect "${company}" and all its analytics?`)) return;
    try {
      await deleteProspect(id);
      setItems((curr) => curr.filter((x) => x.id !== id));
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const handleChangeCode = async (p) => {
    const next = window.prompt(`New access code for ${p.company_name} (letters/numbers, case-insensitive):`, p.code);
    if (next === null) return;
    const code = next.trim();
    if (code.length < 4) { toast.error("Code must be at least 4 characters"); return; }
    try {
      await updateProspectCode(p.id, code);
      setItems((curr) => curr.map((x) => (x.id === p.id ? { ...x, code } : x)));
      toast.success(`Code updated · ${code}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.[0]?.msg || "Could not update code");
    }
  };

  const handleSendLink = async (p) => {
    if (!p.contact_email) {
      toast.error("Add a contact email to this prospect first.");
      return;
    }
    if (!window.confirm(`Send the personalized deck link + code to ${p.contact_email}?`)) return;
    try {
      const r = await sendProspectLink(p.id);
      toast.success(`Sent to ${r.to}`);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Send failed";
      toast.error(detail);
    }
  };

  const headerBtn = "flex items-center gap-2 mono text-[11px] uppercase tracking-[0.22em] text-ink-900/65 hover:text-sun-500 transition-colors px-3 py-2 border border-ink-900/10 rounded-md";

  return (
    <div className="min-h-screen bg-cream-50 text-ink-900 grain" data-testid="admin-page">
      <header className="flex items-center justify-between px-8 md:px-14 py-7 relative z-10">
        <div className="flex items-center gap-6">
          <button onClick={() => nav("/")} data-testid="admin-back"
                  className="flex items-center gap-2 mono text-[11px] uppercase tracking-[0.22em] text-ink-900/65 hover:text-sun-500 transition-colors">
            <ArrowLeft className="h-3.5 w-3.5" /> Back to deck
          </button>
          <div className="hidden md:flex items-center gap-2 text-ink-900/65">
            <img src="/brand/complisun-mark.png" alt="CompliSun" className="h-7 w-7 object-contain" />
            <span className="serif text-sm">CompliSun</span>
            <span className="mono text-[10px] uppercase tracking-[0.22em] ml-1">Admin</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => nav("/admin/leads")} data-testid="admin-leads-link" className={headerBtn}>
            <Star className="h-3.5 w-3.5" /> Leads
          </button>
          <button onClick={() => nav("/admin/edit/portfolio")} data-testid="admin-edit-portfolio-link" className={headerBtn}>
            <Edit3 className="h-3.5 w-3.5" /> Edit Portfolio
          </button>
          <button onClick={() => nav("/admin/edit/core")} data-testid="admin-edit-core-link" className={headerBtn}>
            <Edit3 className="h-3.5 w-3.5" /> Edit Core Deck
          </button>
          <button onClick={() => nav("/inbox")} data-testid="admin-inbox" className={headerBtn}>
            <InboxIcon className="h-3.5 w-3.5" /> Inbox
          </button>
          <button onClick={() => exportProspectsCsv()} data-testid="admin-export-csv" className={headerBtn}>
            Cheat Sheet (CSV)
          </button>
          <button onClick={load} data-testid="admin-refresh" className={headerBtn}>
            <RefreshCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} />
          </button>
        </div>
      </header>

      <main className="px-8 md:px-14 pb-20 relative z-10 max-w-6xl mx-auto">
        <div className="flex items-end justify-between gap-6 mb-10">
          <div>
            <p className="mono text-[11px] uppercase tracking-[0.22em] text-sun-500 mb-3">/ prospects</p>
            <h1 className="serif font-light text-5xl md:text-6xl tracking-tight leading-tight">
              Prospect <em className="italic text-sun-500">links</em>.
            </h1>
            <p className="mt-3 text-ink-900/70 max-w-2xl">
              Each prospect gets a personalized deck URL + unique access code. Track views and time-per-slide.
            </p>
          </div>
          <Button onClick={() => setOpen((v) => !v)} data-testid="new-prospect-btn"
                  className="h-12 px-5 rounded-md bg-sun-500 hover:bg-sun-400 text-ink-900 font-medium gap-2">
            <Plus className="h-4 w-4" /> New prospect
          </Button>
        </div>

        <GiggleCallout />

        {open && (
          <NewProspectForm form={form} setForm={setForm} creating={creating}
                           onSubmit={handleCreate} onCancel={() => setOpen(false)} />
        )}

        {busy && items.length === 0 ? (
          <div className="mono text-sm text-ink-900/55">loading…</div>
        ) : items.length === 0 ? (
          <div className="border border-ink-900/10 rounded-md p-12 text-center bg-white/55" data-testid="empty-prospects">
            <div className="serif text-3xl mb-2">No prospects yet.</div>
            <div className="mono text-[10px] uppercase tracking-[0.22em] text-ink-900/55">
              Click "New prospect" to generate a personalized pitch link.
            </div>
          </div>
        ) : (
          <ul className="space-y-3" data-testid="prospect-list">
            {items.map((p) => (
              <ProspectRow key={p.id} p={p} origin={origin}
                           onCopy={handleCopy} onDelete={handleDelete} onSendLink={handleSendLink}
                           onChangeCode={handleChangeCode} />
            ))}
          </ul>
        )}

        <ActivityFeed activity={activity} busy={activityBusy} onRefresh={loadActivity} />
      </main>
    </div>
  );
}
